import { useEffect } from 'react'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import moment from 'moment'
import styled from 'styled-components/native'

import { useDispatch, useSelector } from 'APP/Hooks/redux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import i18n from 'APP/I18n'
import { selectJourneyData } from 'APP/Store/Journey/selectors'

const StyledSection = styled.View`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

type JourneyScreenProps = NativeStackScreenProps<ReactNavigation.RootParamList, 'journeyScreen'>
const JourneyScreen = ({ route, navigation }: JourneyScreenProps) => {
  const journeyId = route?.params?.id
  const dispatch = useDispatch()

  const { appointments, careTeam, journey, carePlan } = useSelector((state) =>
    selectJourneyData(state, journeyId)
  )

  const { healthIssueTypeId, createdAt, channelId, summary } = journey
  const { carePlan: plan, lastUpdated } = carePlan

  useEffect(() => {
    if ([...appointments.past, ...appointments.upcoming].length === 0)
      dispatch(PatientHistoryActions.refreshAppointmentsRequest())
  }, [])

  if (!healthIssueTypeId)
    return (
      <ResponsiveView>
        <Typography>{i18n.t('JourneyScreen.notFound')}</Typography>
      </ResponsiveView>
    )

  return (
    <ResponsiveScrollView>
      <StyledSection>
        <Typography>
          <Typography variant="bodyNormalBold"> healthIssueTypeId: </Typography>
          {healthIssueTypeId}
        </Typography>
        <Typography>
          <Typography variant="bodyNormalBold"> createdAt: </Typography>
          {moment(createdAt).format('MMMM D, YYYY')}
        </Typography>
        <Typography
          variant="link"
          onPress={() => navigation.navigate('conversation', { channelId })}
        >
          {i18n.t('JourneyScreen.conversation')}
        </Typography>
      </StyledSection>

      <StyledSection>
        <Typography>
          <Typography variant="bodyNormalBold"> {i18n.t('JourneyScreen.summary')}: </Typography>
          {summary}
        </Typography>
      </StyledSection>

      <StyledSection>
        <Typography>
          <Typography variant="bodyNormalBold">
            {i18n.t('JourneyScreen.carePlan')} (
            {i18n.t('JourneyScreen.lastUpdated', {
              0: moment(lastUpdated).format('DD MMM'),
            })}
            ):{' '}
          </Typography>
          {plan}
        </Typography>
      </StyledSection>

      <StyledSection>
        <Typography variant="bodyNormalBold">
          {i18n.t('JourneyScreen.upcomingAppointment')}
        </Typography>
        {appointments.upcoming.map(({ id, start_at, practitioner }) => (
          <Typography key={id}>
            - {moment(start_at).format('MMM D, YYYY h:mm A')} with {practitioner.first_name}{' '}
            {practitioner.last_name}
          </Typography>
        ))}
      </StyledSection>

      <StyledSection>
        <Typography variant="bodyNormalBold"> {i18n.t('JourneyScreen.pastAppointment')}</Typography>
        {appointments.past.map(({ id, start_at, practitioner }) => (
          <Typography key={id}>
            - {moment(start_at).format('MMM D, YYYY h:mm A')} with {practitioner.first_name}{' '}
            {practitioner.last_name}
          </Typography>
        ))}
      </StyledSection>

      <StyledSection>
        <Typography variant="bodyNormalBold"> {i18n.t('JourneyScreen.careTeam')}</Typography>
        {careTeam.map(({ id, first_name, last_name }) => (
          <Typography key={id}>
            - {first_name} {last_name}
          </Typography>
        ))}
      </StyledSection>
    </ResponsiveScrollView>
  )
}

export default JourneyScreen
